/**
 * useVehiclesLibrary — 2026-06-08 Phase 2E wire-up for the canonical
 * Q Manual VG35002 vehicles + vehicle-modifications catalogs.
 *
 * Manages BOTH the vehicles manifest (92 entries) and the vehicle-
 * modifications manifest (31 entries) since the SPA's Phase 2E drawer
 * (Option A — per user's PRE-COMPACT-18 §5.4 decision) renders them as
 * 4 internal tabs (Land / Air / Water / Modifications).
 *
 * 3-state union per MEMORY.md rule `feedback_async_then_render_needs_loading_state`:
 * tracks idle/loading/ready/error independently for vehicles manifest +
 * modifications manifest + per-vehicle detail + per-mod detail. Tab state
 * stays in this hook so the toggle button can preserve the user's last
 * selection across drawer open/close cycles.
 *
 * Mirrors useEquipmentLibrary.ts (Phase 1E gold standard) — injected
 * service modules for test stubbing + useMemo-stabilized default instances
 * + cancelled-flag for unmount safety.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import type {
  VehicleManifestEntry,
} from '../services/host/VehiclesServiceModule';
import { VehiclesServiceModule } from '../services/host/VehiclesServiceModule';
import type {
  ModificationManifestEntry,
} from '../services/host/VehicleModificationsServiceModule';
import { VehicleModificationsServiceModule } from '../services/host/VehicleModificationsServiceModule';

export type VehiclesLibraryState = 'idle' | 'loading' | 'ready' | 'error';

export type VehiclesLibraryTab = 'land' | 'air' | 'water' | 'modifications';

export interface VehiclesLibraryHookResult {
  // Vehicles manifest
  vehiclesState: VehiclesLibraryState;
  vehicles: VehicleManifestEntry[];
  vehiclesError: string | null;
  refreshVehicles: () => void;

  // Modifications manifest
  modificationsState: VehiclesLibraryState;
  modifications: ModificationManifestEntry[];
  modificationsError: string | null;
  refreshModifications: () => void;

  // Active tab
  activeTab: VehiclesLibraryTab;
  setActiveTab: (tab: VehiclesLibraryTab) => void;

  // Per-vehicle detail
  selectedVehicleId: string | null;
  selectedVehicleData: Record<string, unknown> | null;
  selectedVehicleState: VehiclesLibraryState;
  selectVehicle: (vehicleId: string | null) => void;

  // Per-modification detail
  selectedModificationId: string | null;
  selectedModificationData: Record<string, unknown> | null;
  selectedModificationState: VehiclesLibraryState;
  selectModification: (modId: string | null) => void;
}

export function useVehiclesLibrary(
  vehiclesService?: VehiclesServiceModule,
  modificationsService?: VehicleModificationsServiceModule,
): VehiclesLibraryHookResult {
  const resolvedVehiclesService = useMemo(
    () => vehiclesService ?? new VehiclesServiceModule(),
    [vehiclesService],
  );
  const resolvedModsService = useMemo(
    () => modificationsService ?? new VehicleModificationsServiceModule(),
    [modificationsService],
  );

  // Vehicles manifest state
  const [vehiclesState, setVehiclesState] =
    useState<VehiclesLibraryState>('idle');
  const [vehicles, setVehicles] = useState<VehicleManifestEntry[]>([]);
  const [vehiclesError, setVehiclesError] = useState<string | null>(null);
  const [vehiclesRefreshTick, setVehiclesRefreshTick] = useState(0);

  // Modifications manifest state
  const [modificationsState, setModificationsState] =
    useState<VehiclesLibraryState>('idle');
  const [modifications, setModifications] = useState<
    ModificationManifestEntry[]
  >([]);
  const [modificationsError, setModificationsError] = useState<string | null>(
    null,
  );
  const [modsRefreshTick, setModsRefreshTick] = useState(0);

  // Tab state — default 'land' (most-used category)
  const [activeTab, setActiveTab] = useState<VehiclesLibraryTab>('land');

  // Per-vehicle detail state
  const [selectedVehicleId, setSelectedVehicleId] = useState<string | null>(
    null,
  );
  const [selectedVehicleData, setSelectedVehicleData] = useState<
    Record<string, unknown> | null
  >(null);
  const [selectedVehicleState, setSelectedVehicleState] =
    useState<VehiclesLibraryState>('idle');

  // Per-modification detail state
  const [selectedModificationId, setSelectedModificationId] = useState<
    string | null
  >(null);
  const [selectedModificationData, setSelectedModificationData] = useState<
    Record<string, unknown> | null
  >(null);
  const [selectedModificationState, setSelectedModificationState] =
    useState<VehiclesLibraryState>('idle');

  const refreshVehicles = useCallback(() => {
    setVehiclesRefreshTick((n) => n + 1);
  }, []);

  const refreshModifications = useCallback(() => {
    setModsRefreshTick((n) => n + 1);
  }, []);

  const selectVehicle = useCallback((vehicleId: string | null) => {
    setSelectedVehicleId(vehicleId);
    if (vehicleId === null) {
      setSelectedVehicleData(null);
      setSelectedVehicleState('idle');
    }
  }, []);

  const selectModification = useCallback((modId: string | null) => {
    setSelectedModificationId(modId);
    if (modId === null) {
      setSelectedModificationData(null);
      setSelectedModificationState('idle');
    }
  }, []);

  // Vehicles manifest fetch
  useEffect(() => {
    let cancelled = false;

    setVehiclesState('loading');
    setVehiclesError(null);

    (async () => {
      try {
        const result = await resolvedVehiclesService.list();
        if (cancelled) return;
        setVehicles(result.vehicles);
        setVehiclesState('ready');
      } catch (e) {
        if (cancelled) return;
        setVehicles([]);
        setVehiclesError(e instanceof Error ? e.message : String(e));
        setVehiclesState('error');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [resolvedVehiclesService, vehiclesRefreshTick]);

  // Modifications manifest fetch
  useEffect(() => {
    let cancelled = false;

    setModificationsState('loading');
    setModificationsError(null);

    (async () => {
      try {
        const result = await resolvedModsService.list();
        if (cancelled) return;
        setModifications(result.modifications);
        setModificationsState('ready');
      } catch (e) {
        if (cancelled) return;
        setModifications([]);
        setModificationsError(e instanceof Error ? e.message : String(e));
        setModificationsState('error');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [resolvedModsService, modsRefreshTick]);

  // Per-vehicle detail fetch — fires when selectedVehicleId changes
  useEffect(() => {
    if (selectedVehicleId === null) {
      return;
    }
    let cancelled = false;

    setSelectedVehicleState('loading');
    setSelectedVehicleData(null);

    (async () => {
      try {
        const result = await resolvedVehiclesService.get(selectedVehicleId);
        if (cancelled) return;
        if (result === null) {
          setSelectedVehicleState('error');
          return;
        }
        setSelectedVehicleData(result);
        setSelectedVehicleState('ready');
      } catch {
        if (cancelled) return;
        setSelectedVehicleState('error');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [resolvedVehiclesService, selectedVehicleId]);

  // Per-modification detail fetch — fires when selectedModificationId changes
  useEffect(() => {
    if (selectedModificationId === null) {
      return;
    }
    let cancelled = false;

    setSelectedModificationState('loading');
    setSelectedModificationData(null);

    (async () => {
      try {
        const result = await resolvedModsService.get(selectedModificationId);
        if (cancelled) return;
        if (result === null) {
          setSelectedModificationState('error');
          return;
        }
        setSelectedModificationData(result);
        setSelectedModificationState('ready');
      } catch {
        if (cancelled) return;
        setSelectedModificationState('error');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [resolvedModsService, selectedModificationId]);

  return {
    vehiclesState,
    vehicles,
    vehiclesError,
    refreshVehicles,
    modificationsState,
    modifications,
    modificationsError,
    refreshModifications,
    activeTab,
    setActiveTab,
    selectedVehicleId,
    selectedVehicleData,
    selectedVehicleState,
    selectVehicle,
    selectedModificationId,
    selectedModificationData,
    selectedModificationState,
    selectModification,
  };
}
